import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import styles from "./MyOrders.module.css";
import Navbar from "components/Navbar";
import BottomNav from "components/BottomNav";
import FilterMenu from "components/FilterMenu";
import { useOrders } from "../../hooks/useOrders";
import { useCart, getCartItemCount } from "../../hooks/useCart";
import { useOrderBuyAgain } from "../../hooks/useOrderActions";
import { trackProductAdded } from "../../utils/analytics";

const MAX_VISIBLE_ITEM_IMAGES = 3;

const formatMonthYear = (dateString) => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const year = date.getFullYear();
  return `${month} – ${year}`;
};

const formatDateWithSuffix = (dateString) => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();

  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";

  return (
    <>
      {month} {day}
      <sup>{suffix}</sup>
    </>
  );
};

export function MyOrders() {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("My Orders");
  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [monthFilter, setMonthFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [brokenImages, setBrokenImages] = useState({});

  // Browsers don't reliably constrain the fallback alt-text box for a failed
  // <img>, so swap to a fixed-size placeholder instead of trusting the
  // broken image's own rendering.
  const renderItemImage = (imageUrl, title, id, className) => {
    if (brokenImages[id]) {
      return (
        <div
          className={`${className} ${styles.imagePlaceholder}`}
          title={title}
          role="img"
          aria-label={title}
        >
          {title}
        </div>
      );
    }

    return (
      <img
        src={imageUrl}
        alt={title}
        className={className}
        onError={() =>
          setBrokenImages((prev) => (prev[id] ? prev : { ...prev, [id]: true }))
        }
      />
    );
  };

  const params = {};
  if (yearFilter !== "all") params.year = yearFilter;
  if (monthFilter !== "all") params.month = monthFilter;

  const { data: orders = [], isLoading } = useOrders(params);
  const { data: cartData } = useCart();
  const cartCount = getCartItemCount(cartData);
  const buyAgain = useOrderBuyAgain();

  const menuItems = [
    { label: "WWG Store", href: "/wwg-store" },
    { label: "My Lists", href: "/my-lists" },
    { label: "My Orders", href: "/my-orders" },
    { label: "Cart", href: "/cart" },
  ];

  const handleTabClick = (item) => {
    setActiveTab(item.label);
    history.push(item.href);
  };

  const preOrders = orders.filter((o) => o.isPreOrder);
  const completedOrders = orders.filter((o) => !o.isPreOrder);

  // The mock-data path and (potentially) the real API don't reliably filter
  // by year/month/search, so re-apply the filters client-side to guarantee
  // the FilterMenu controls always take effect.
  const matchesYearMonth = (order) => {
    if (!order.date) return true;
    const parsedDate = new Date(order.date);
    if (Number.isNaN(parsedDate.getTime())) return true;
    if (
      yearFilter !== "all" &&
      parsedDate.getFullYear() !== Number(yearFilter)
    ) {
      return false;
    }
    if (
      monthFilter !== "all" &&
      parsedDate.getMonth() !== Number(monthFilter)
    ) {
      return false;
    }
    return true;
  };

  const matchesSearch = (order) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const haystack = [
      order.id,
      order.tracking,
      ...(order.items ?? []).map((item) => item.title),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(term);
  };

  const filteredPreOrders = preOrders.filter(
    (o) => matchesYearMonth(o) && matchesSearch(o)
  );
  const filteredCompletedOrders = completedOrders.filter(
    (o) => matchesYearMonth(o) && matchesSearch(o)
  );

  const groupedOrders = filteredCompletedOrders.reduce((acc, order) => {
    const monthYear = formatMonthYear(order.date);
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(order);
    return acc;
  }, {});

  const renderOrder = (order, isLast) => {
    const isSingleItem = (order.items ?? []).length === 1;

    const handleCancelPreOrder = (event) => {
      event.stopPropagation();
      const queryStr = order.source === "new" ? "?isNew=true" : "";
      history.push(`/order/${order.id}/cancel${queryStr}`);
    };

    const handleEditShipping = (event) => {
      event.stopPropagation();
      const queryStr = order.source === "new" ? "?isNew=true" : "";
      history.push(`/order/${order.id}/edit-shipping${queryStr}`);
    };

    const handleBuyAgain = (event) => {
      event.stopPropagation();
      buyAgain.mutate(order.id, {
        onSuccess: (data) => {
          const skippedProductIds = new Set(
            (data?.skippedProductIds ?? []).map(String)
          );
          const skippedSkus = new Set((data?.skippedSkus ?? []).map(String));

          (order.items ?? [])
            .filter((item) => {
              const productId = item?.productId ?? item?.stockId;
              return (
                !skippedProductIds.has(String(productId)) &&
                !skippedSkus.has(String(item?.sku))
              );
            })
            .forEach((item) =>
              trackProductAdded(item, {
                quantity: item?.quantity,
                reOrder: true,
              })
            );

          history.push("/cart");
        },
      });
    };

    return (
      <div
        key={order.id}
        className={styles.orderBlock}
        onClick={() => {
          const queryStr = order.source === "new" ? "?isNew=true" : "";
          history.push(`/order/${order.id}${queryStr}`);
        }}
      >
        {isSingleItem ? (
          <div className={styles.singleItemWrapper}>
            {renderItemImage(
              order.items[0].imageUrl,
              order.items[0].title,
              `single-${order.id}`,
              styles.singleItemImage
            )}

            <div className={styles.singleItemDetails}>
              <div className={styles.orderId}>Order # {order.id}</div>

              {!order.isPreOrder && (
                <div className={styles.tracking}>
                  Tracking #: {order.tracking}
                </div>
              )}

              <div className={styles.date}>
                {formatDateWithSuffix(order.date)}
              </div>

              {!order.isPreOrder && (
                <button
                  type="button"
                  className={styles.buyAgainButton}
                  onClick={handleBuyAgain}
                >
                  Buy Again
                </button>
              )}
            </div>

            <div className={styles.singleItemPrice}>
              {order.total != null ? `$${order.total.toFixed(2)}` : "N/A"}
            </div>
          </div>
        ) : (
          <>
            <div className={styles.orderHeaderRow}>
              <div>
                <div className={styles.orderId}>Order # {order.id}</div>

                {!order.isPreOrder && (
                  <div className={styles.tracking}>
                    Tracking #: {order.tracking}
                  </div>
                )}

                <div className={styles.date}>
                  {formatDateWithSuffix(order.date)}
                </div>
              </div>

              <div className={styles.orderPrice}>
                {order.total != null ? `$${order.total.toFixed(2)}` : "N/A"}
              </div>
            </div>

            <div className={styles.imageList}>
              {(order.items ?? [])
                .slice(0, MAX_VISIBLE_ITEM_IMAGES)
                .map((item, index) => {
                  const rowKey = `${item.stockId ?? "item"}-${index}`;
                  return (
                    <React.Fragment key={rowKey}>
                      {renderItemImage(
                        item.imageUrl,
                        item.title,
                        rowKey,
                        styles.itemImage
                      )}
                    </React.Fragment>
                  );
                })}

              {order.items.length > MAX_VISIBLE_ITEM_IMAGES && (
                <div className={styles.moreItemsText}>
                  ... and +{order.items.length - MAX_VISIBLE_ITEM_IMAGES} more{" "}
                  {order.items.length - MAX_VISIBLE_ITEM_IMAGES === 1
                    ? "item"
                    : "items"}
                </div>
              )}
            </div>
          </>
        )}

        {order.isPreOrder && (
          <div className={styles.preOrderActions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleCancelPreOrder}
            >
              Cancel Pre-order
            </button>

            <button
              type="button"
              className={styles.editShippingButton}
              onClick={handleEditShipping}
            >
              Edit Shipping
            </button>
          </div>
        )}

        {!isLast && <div className={styles.divider}></div>}
      </div>
    );
  };

  const yearOptions = [
    { label: currentYear, value: currentYear },
    { label: currentYear - 1, value: currentYear - 1 },
    { label: currentYear - 2, value: currentYear - 2 },
    { label: currentYear - 3, value: currentYear - 3 },
    { label: currentYear - 4, value: currentYear - 4 },
    { label: currentYear - 5, value: currentYear - 5 },
    { label: "All", value: "all" },
  ];

  const monthOptions = [
    { label: "All", value: "all" },
    { label: "January", value: 0 },
    { label: "February", value: 1 },
    { label: "March", value: 2 },
    { label: "April", value: 3 },
    { label: "May", value: 4 },
    { label: "June", value: 5 },
    { label: "July", value: 6 },
    { label: "August", value: 7 },
    { label: "September", value: 8 },
    { label: "October", value: 9 },
    { label: "November", value: 10 },
    { label: "December", value: 11 },
  ];

  return (
    <div className={styles.container}>
      {/* Desktop-only top nav */}
      <div className={styles.desktopNav}>
        <Navbar
          onClick={() => {}}
          isHome={true}
          menuItems={menuItems}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          cartCount={cartCount}
        />
      </div>

      <div className={styles.filterMenu}>
        <FilterMenu
          backButton={true}
          handleBackClick={() => alert("Back button Clicked")}
          dropdownOne={yearOptions}
          dropdownTwo={monthOptions}
          selectedOneValue={yearFilter}
          selectedTwoValue={monthFilter}
          handlePickOne={(val) => setYearFilter(val)}
          handlePickTwo={(val) => setMonthFilter(val)}
          onSearchInput={(term) => setSearchTerm(term)}
          filterButton={false}
        />
      </div>

      <div className={styles.preOrdersClip}>
        <motion.div
          key={`${
            isLoading ? "loading" : "loaded"
          }-${yearFilter}-${monthFilter}-${searchTerm}`}
          className={styles.preOrders}
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
        >
          {!isLoading &&
            filteredPreOrders.length === 0 &&
            Object.keys(groupedOrders).length === 0 && (
              <div className={styles.noResults}>
                No items matching filter terms
              </div>
            )}

          {filteredPreOrders.length > 0 && (
            <div className={styles.section}>
              <h2 className={styles.preOrderHeader}>Pre-Orders</h2>
              {filteredPreOrders.map((order, index) =>
                renderOrder(order, index === filteredPreOrders.length - 1)
              )}
            </div>
          )}

          {Object.keys(groupedOrders).map((monthYear) => (
            <div key={monthYear} className={styles.section}>
              <h2 className={styles.completedHeader}>{monthYear}</h2>
              {groupedOrders[monthYear].map((order, index) =>
                renderOrder(
                  order,
                  index === groupedOrders[monthYear].length - 1
                )
              )}
            </div>
          ))}
        </motion.div>
      </div>

      {/* Mobile-only bottom nav */}
      <BottomNav cartCount={cartCount} />
    </div>
  );
}

export default MyOrders;
